import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ShieldX, Clock, Search, ChevronLeft, CheckCircle2, XCircle,
  User, Building2, RefreshCw, Loader2,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminKyc — /admin/kyc · Super Admin uniquement.
 * Vue file d'attente + approve/reject des dossiers KYC/KYB.
 * Additif strict.
 */
export default function AdminKyc() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: "pending", type: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [motif, setMotif] = useState("");
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [list, st] = await Promise.all([
        api.get("/v1/kyc/admin/list", { params }),
        api.get("/v1/kyc/admin/stats"),
      ]);
      setItems(list.data.items || []);
      setCounts(list.data.counts || {});
      setStats(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const act = async (action) => {
    if (!selected) return;
    if (action === "reject" && !motif.trim()) {
      toast.error("Un motif est obligatoire pour rejeter");
      return;
    }
    setActing(true);
    try {
      await api.patch(`/v1/kyc/admin/${selected.user_id}`, { action, motif: motif.trim() || null });
      toast.success(action === "approve" ? "Dossier approuvé" : "Dossier rejeté");
      setSelected(null); setMotif("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActing(false); }
  };

  if (!isAdmin) {
    return <PrivateLayout title="KYC"><div className="afr-empty" data-testid="kyc-admin-forbidden"><div className="afr-empty__title">Accès restreint</div></div></PrivateLayout>;
  }

  return (
    <PrivateLayout
      title="🛡️ Vérifications KYC / KYB"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "KYC / KYB" }]}
      actions={<Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>}
      data-testid="admin-kyc-page"
    >
      {/* KPIs */}
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }} data-testid="kyc-stats">
        <Kpi Icon={Clock} label="En attente" value={counts.pending || 0} accent="#B45309" />
        <Kpi Icon={ShieldCheck} label="Approuvés" value={counts.approved || 0} accent="#15803D" />
        <Kpi Icon={ShieldX} label="Rejetés" value={counts.rejected || 0} accent="#B91C1C" />
        <Kpi Icon={User} label="KYC" value={stats?.kyc_count || 0} accent="#0F2A42" />
        <Kpi Icon={Building2} label="KYB" value={stats?.kyb_count || 0} accent="#0F2A42" />
      </div>

      {/* Filtres */}
      <article className="afr-card" style={{ padding: 12, marginBottom: 16 }} data-testid="kyc-filters">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {["pending", "approved", "rejected", ""].map((s) => (
            <button key={s} type="button" onClick={() => setFilters((f) => ({ ...f, status: s }))}
              className={`afr-btn afr-btn--sm ${filters.status === s ? "afr-btn--primary" : "afr-btn--outline"}`}
              data-testid={`kyc-filter-status-${s || "all"}`}>
              {s ? { pending: "En attente", approved: "Approuvés", rejected: "Rejetés" }[s] : "Tous"}
            </button>
          ))}
          <span style={{ borderLeft: "1px solid var(--afr-border)", height: 20, margin: "0 6px" }} />
          {["", "kyc", "kyb"].map((t) => (
            <button key={t || "any"} type="button" onClick={() => setFilters((f) => ({ ...f, type: t }))}
              className={`afr-btn afr-btn--sm ${filters.type === t ? "afr-btn--primary" : "afr-btn--outline"}`}
              data-testid={`kyc-filter-type-${t || "all"}`}>
              {t ? t.toUpperCase() : "Tous types"}
            </button>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <Search size={12} />
            <input placeholder="Nom, email…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              data-testid="kyc-filter-search" style={{ padding: "4px 8px", fontSize: "0.82rem", border: "1px solid var(--afr-border)", borderRadius: 6 }} />
            <button className="afr-btn afr-btn--sm afr-btn--outline" onClick={load} data-testid="kyc-refresh">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
      </article>

      {loading ? (
        <div data-testid="kyc-admin-loading">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="afr-empty" data-testid="kyc-admin-empty"><div className="afr-empty__title">Aucun dossier</div></div>
      ) : (
        <table className="afr-table" data-testid="kyc-admin-table">
          <thead>
            <tr>
              <th>Utilisateur</th><th>Rôle</th><th>Type</th><th>Statut</th><th>Soumis</th><th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`kyc-row-${it.user_id}`}>
                <td>
                  <div style={{ fontWeight: 600 }}>{it.user_name}</div>
                  <div className="afr-muted" style={{ fontSize: "0.76rem" }}>{it.user_email}</div>
                </td>
                <td><span className="afr-badge">{it.user_role}</span></td>
                <td>
                  <span className="afr-badge" style={{ background: it.type === "kyb" ? "rgba(15,42,66,0.10)" : "rgba(0,150,64,0.10)" }}>
                    {(it.type || "?").toUpperCase()}
                  </span>
                </td>
                <td><StatusBadge status={it.status} /></td>
                <td style={{ fontSize: "0.78rem" }}>{it.submitted_at ? new Date(it.submitted_at).toLocaleString("fr-FR") : "—"}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="afr-btn afr-btn--sm afr-btn--outline" onClick={() => { setSelected(it); setMotif(""); }} data-testid={`kyc-open-${it.user_id}`}>
                    Voir le dossier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <Modal onClose={() => setSelected(null)} title={`Dossier ${(selected.type || "").toUpperCase()} — ${selected.user_name}`}>
          <div data-testid="kyc-detail-modal" style={{ display: "grid", gap: 6, fontSize: "0.88rem" }}>
            <Info label="Email" value={selected.user_email} />
            <Info label="Rôle" value={selected.user_role} />
            <Info label="Statut" value={<StatusBadge status={selected.status} />} />
            <Info label="Soumis" value={selected.submitted_at ? new Date(selected.submitted_at).toLocaleString("fr-FR") : "—"} />
            {selected.motif && <Info label="Motif précédent" value={<span style={{ color: "#B91C1C" }}>{selected.motif}</span>} />}
            <hr style={{ margin: "10px 0", opacity: 0.4 }} />
            {selected.type === "kyc" && selected.personal && (
              <>
                <Info label="Nom complet" value={selected.personal.full_name} />
                <Info label="Date de naissance" value={selected.personal.date_of_birth} />
                <Info label="Nationalité" value={selected.personal.nationality} />
                <Info label="Résidence" value={selected.personal.country_residence} />
                <Info label="Pièce" value={`${selected.personal.id_type} · ${selected.personal.id_number}`} />
                {selected.personal.phone && <Info label="Téléphone" value={selected.personal.phone} />}
                {selected.personal.address && <Info label="Adresse" value={selected.personal.address} />}
              </>
            )}
            {selected.type === "kyb" && selected.business && (
              <>
                <Info label="Raison sociale" value={selected.business.legal_name} />
                {selected.business.trade_name && <Info label="Nom commercial" value={selected.business.trade_name} />}
                <Info label="N° d'enregistrement" value={selected.business.registration_number} />
                <Info label="Pays d'immatriculation" value={selected.business.incorporation_country} />
                <Info label="Représentant légal" value={`${selected.business.legal_rep_name} (${selected.business.legal_rep_role})`} />
                {selected.business.sector && <Info label="Secteur" value={selected.business.sector} />}
                {selected.business.tax_id && <Info label="Identifiant fiscal" value={selected.business.tax_id} />}
                {selected.business.address && <Info label="Siège" value={selected.business.address} />}
              </>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <textarea placeholder="Motif (obligatoire en cas de rejet)" value={motif} onChange={(e) => setMotif(e.target.value)}
              data-testid="kyc-motif" rows={3}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid var(--afr-border)", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => setSelected(null)} data-testid="kyc-cancel">Annuler</button>
            <button className="afr-btn afr-btn--outline afr-btn--sm" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}
              disabled={acting} onClick={() => act("reject")} data-testid="kyc-reject-btn">
              {acting ? <Loader2 size={12} className="afr-spin" /> : <XCircle size={12} />} Rejeter
            </button>
            <button className="afr-btn afr-btn--primary afr-btn--sm"
              disabled={acting} onClick={() => act("approve")} data-testid="kyc-approve-btn">
              {acting ? <Loader2 size={12} className="afr-spin" /> : <CheckCircle2 size={12} />} Approuver
            </button>
          </div>
        </Modal>
      )}
    </PrivateLayout>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    pending:  { bg: "rgba(180,83,9,0.12)",  color: "#B45309", Icon: Clock,       label: "En attente" },
    approved: { bg: "rgba(21,128,61,0.14)", color: "#15803D", Icon: ShieldCheck, label: "Approuvé" },
    rejected: { bg: "rgba(185,28,28,0.10)", color: "#B91C1C", Icon: ShieldX,     label: "Rejeté" },
    not_started: { bg: "rgba(15,42,66,0.10)", color: "#0F2A42", Icon: User,      label: "Non démarré" },
  }[status] || { bg: "#eee", color: "#333", Icon: User, label: status };
  return (
    <span className="afr-badge" style={{ background: cfg.bg, color: cfg.color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <cfg.Icon size={11} /> {cfg.label}
    </span>
  );
}

function Info({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <span className="afr-muted" style={{ minWidth: 160, fontSize: "0.78rem" }}>{label}</span>
      <span>{value ?? "—"}</span>
    </div>
  );
}

function Kpi({ Icon, label, value, accent }) {
  return (
    <div className="afr-kpi" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
    </div>
  );
}

function Modal({ onClose, title, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,42,66,0.55)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <article style={{
        background: "var(--afr-surface)", padding: 20, borderRadius: 12,
        width: "min(720px, 100%)", maxHeight: "90vh", overflow: "auto",
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={onClose}>Fermer</button>
        </div>
        {children}
      </article>
    </div>
  );
}
